/**
 * DocumentVersionHistory Component
 * 
 * Displays version history for a document with restore capability
 */

import React, { useState } from 'react';
import { DocumentVersion } from '../types/documentsTypes';
import { 
  History, Download, RotateCcw, ChevronDown, ChevronUp,
  FileText, User, Calendar, HardDrive
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

export interface DocumentVersionHistoryProps {
  versions: DocumentVersion[];
  currentVersion: number;
  onDownload: (version: DocumentVersion) => void;
  onRestore: (version: DocumentVersion) => void;
  isLoading?: boolean;
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ============================================================================
// Subcomponents
// ============================================================================

interface VersionItemProps {
  version: DocumentVersion;
  isCurrent: boolean;
  onDownload: () => void;
  onRestore: () => void;
  isRestoring?: boolean;
}

const VersionItem: React.FC<VersionItemProps> = ({
  version,
  isCurrent,
  onDownload,
  onRestore,
  isRestoring = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cn(
      'border rounded-lg transition-colors',
      isCurrent ? 'border-sage bg-sage/5' : 'border-gray-200 bg-white'
    )}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
          isCurrent ? 'bg-sage text-white' : 'bg-cream text-warm-gray'
        )}>
          <FileText className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-navy">
              Version {version.version_number}
            </span>
            {isCurrent && (
              <span className="px-2 py-0.5 bg-sage text-white text-xs rounded-full">
                Current
              </span>
            )}
          </div>
          <p className="text-sm text-warm-gray truncate">
            {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
          </p>
        </div>

        {expanded ? (
          <ChevronUp className="w-5 h-5 text-warm-gray" />
        ) : (
          <ChevronDown className="w-5 h-5 text-warm-gray" />
        )}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 mt-0 pt-4">
          {/* Details */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-warm-gray" />
              <span className="text-warm-gray">Uploaded by:</span>
              <span className="text-navy">{version.uploaded_by_name || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <HardDrive className="w-4 h-4 text-warm-gray" />
              <span className="text-warm-gray">Size:</span>
              <span className="text-navy">{formatFileSize(version.file_size_bytes)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm col-span-2">
              <Calendar className="w-4 h-4 text-warm-gray" />
              <span className="text-warm-gray">Uploaded:</span>
              <span className="text-navy">
                {format(new Date(version.created_at), 'MMM d, yyyy \'at\' h:mm a')}
              </span>
            </div>
          </div>

          {/* Change Notes */}
          {version.change_notes && (
            <div className="mb-4">
              <p className="text-sm text-warm-gray mb-1">Change notes:</p>
              <p className="text-sm text-navy bg-cream rounded p-2">
                {version.change_notes}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-cream transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            {!isCurrent && (
              <button
                onClick={(e) => { e.stopPropagation(); onRestore(); }}
                disabled={isRestoring}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors',
                  'bg-sage text-white hover:bg-sage-dark',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <RotateCcw className="w-4 h-4" />
                {isRestoring ? 'Restoring...' : 'Restore'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const DocumentVersionHistory: React.FC<DocumentVersionHistoryProps> = ({
  versions,
  currentVersion,
  onDownload,
  onRestore,
  isLoading = false,
  className,
}) => {
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);

  // Sort versions by version number descending (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);

  const handleRestore = async (version: DocumentVersion) => {
    setRestoringVersion(version.version_number);
    try {
      await onRestore(version);
    } finally {
      setRestoringVersion(null);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className={cn('text-center py-8', className)}>
        <History className="w-12 h-12 mx-auto text-warm-gray opacity-50 mb-3" />
        <p className="text-warm-gray">No version history available</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-warm-gray" />
        <h3 className="font-medium text-navy">
          Version History ({versions.length})
        </h3>
      </div>

      {sortedVersions.map(version => (
        <VersionItem
          key={version.id}
          version={version}
          isCurrent={version.version_number === currentVersion}
          onDownload={() => onDownload(version)}
          onRestore={() => handleRestore(version)}
          isRestoring={restoringVersion === version.version_number}
        />
      ))}
    </div>
  );
};

export default DocumentVersionHistory;

